import { apiClient } from "@/api/api-client";
import { CreateBomModal } from "@/components/bom/create-bom-modal";
import { EditBomModal } from "@/components/bom/edit-bom-modal";
import { GenerateBomModal } from "@/components/bom/generate-bom-modal";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import { Content } from "@/components/layouts/content";
import { bomsQueryOptions, useBomDeleteMutation } from "@/features/dashboard/api/queries/bom-query";
import { generatedBomsQueryOptions, useGeneratedBomDeleteMutation } from "@/features/dashboard/api/queries/generated-bom-query";
import type { Bom } from "@/features/dashboard/types/bom";
import type { GeneratedBom } from "@/features/dashboard/types/generated-bom";
import { queryClient } from "@/query-client";
import { formatMarkupRate, formatNumber } from "@/utils/format-number";
import { openBomPdfInNewTab, prepareBomDataForExport } from "@/utils/export-data";
import { IconDownload, IconEye, IconFileTypePdf, IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { view } from "framer-motion";

const columns: ColumnDef<Bom>[] = [
	{
		id: "item_name",
		label: "item name",
		cellClassName: "min-w-[250px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const itemName = data.item_name;
			return (
				<div className="flex flex-col space-y-1">
					{itemName ? (
						<div className="flex flex-col">
							<span className="font-semibold text-blue-600">{data.item_name}</span>
							<span className="text-gray-500"> Supplier : {data.supplier_name}</span>
						</div>
					) : (
						<div className="flex flex-col">
							<span className="font-semibold text-blue-600">-</span>
							<span className="text-gray-500"> Supplier : {data.supplier_name}</span>
						</div>
					)}
				</div>
			)
		},
	},
	{
		id: "specifications",
		label: "specifications",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const specs =
				typeof data.specifications === "string"
					? JSON.parse(data.specifications) // parse if string
					: data.specifications; // use directly if object
			return (
				<div className="flex flex-col space-y-1">
					{specs && Object.keys(specs).length > 0 ? (
						Object.entries(specs).map(([key, value]) => (
							<div key={key} className="flex gap-2 text-sm">
								<span className="font-semibold capitalize">{key}:</span>
								<span>{value}</span>
							</div>
						))
					) : (
						<span className="text-gray-400 text-center">-</span>
					)}
				</div>
			);
		},
	},
	{
		id: "brand_model",
		label: "brand model",
		cellClassName: "min-w-[80px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const value = Array.isArray(data.brand_model) ? data.brand_model.join(", ") : data.brand_model;
			return (
				<div className="flex flex-col space-y-1">
					{value ? (
						<span className="font-medium">{value}</span>
					) : (
						<span className="text-gray-500 text-center">-</span>
					)}
				</div>
			)
		},
	},
	{
		id: "warranty",
		label: "warranty",
		cellClassName: "min-w-[80px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			return (
				<div>
					{data.warranty ? (
						<div className="font-medium">
							{data.warranty} {data.period}{data.warranty > 1 ? "s" : ""}
						</div>
					) : (
						<div className="font-medium text-center">
							-
						</div>
					)}

				</div>
			)

		},
	},
	{
		id: "quantity",
		label: "quantity",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.quantity}</span>,
	},
	{
		id: "unit",
		label: "unit",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.unit}</span>,
	},
	{
		id: "supplier_unit_cost",
		label: "supplier unit cost",
		cellClassName: "min-w-[80px] text-right",
		headerClassName: "whitespace-nowrap text-left",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.supplier_unit_cost)}</span>,
	},
	{
		id: "supplier_total_cost",
		label: "supplier total cost",
		cellClassName: "min-w-[80px] text-right",
		headerClassName: "whitespace-nowrap text-left",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.supplier_total_cost)}</span>,
	},
	{
		id: "additional_costs",
		label: "additional costs",
		cellClassName: "min-w-[80px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const costs =
				typeof data.additional_costs === "string"
					? JSON.parse(data.additional_costs) // parse if string
					: data.additional_costs; // use directly if object
			return (
				<div className="flex flex-col space-y-1">
					{costs && Object.keys(costs).length > 0 ? (
						Object.entries(costs).map(([key, value]) => (
							<div key={key} className="flex gap-2 text-sm">
								<span className="font-semibold capitalize">{key}:</span>
								<span>₱{value}</span>
							</div>
						))
					) : (
						<span className="text-gray-500 text-center">-</span>
					)}
				</div>
			);
		},
	},
	// markup rate
	{
		id: "markup_rate",
		label: "markup rate",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-left",
		cell: ({ data }) => <span className="font-medium">{formatMarkupRate(data.markup_rate)}</span>,
	},
	{
		id: "company_unit_cost",
		label: "company unit cost",
		cellClassName: "min-w-[80px] text-right",
		headerClassName: "whitespace-nowrap text-left",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.company_unit_cost)}</span>,
	},
	{
		id: "company_total_cost",
		label: "company total cost",
		cellClassName: "min-w-[80px] text-right",
		headerClassName: "whitespace-nowrap text-left",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.company_total_cost)}</span>,
	},
];

async function downloadGeneratedBomPdf(projectName: string, items: Record<string, unknown>[]) {
	const selectedIds = items
		.map((i) => i.id)
		.filter((id) => id !== null && id !== undefined && String(id).length > 0)
		.map(String)
		.join(",");

	if (!selectedIds) return;

	const response = await apiClient.post(
		"/bom/export/pdf",
		{ project_title: projectName, selected_ids: selectedIds },
		{ responseType: "blob" },
	);

	const safeName = (projectName || "Report").replace(/\s+/g, "_");
	const url = URL.createObjectURL(response.data);
	const a = document.createElement("a");
	a.href = url;
	a.download = `BOM_${safeName}.pdf`;
	a.click();
	URL.revokeObjectURL(url);
}
async function viewGeneratedBomPdf(projectName: string, items: Record<string, unknown>[]) {
    const selectedIds = items
        .map((i) => i.id)
        .filter((id) => id !== null && id !== undefined && String(id).length > 0)
        .map(String)
        .join(",");

    if (!selectedIds) return;

    const response = await apiClient.post(
        "/bom/export/pdf",
        { project_title: projectName, selected_ids: selectedIds },
        { responseType: "blob" },
    );

    const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
}

const generatedBomColumns: ColumnDef<GeneratedBom>[] = [
	{
		id: "project_name",
		label: "project name",
		cellClassName: "min-w-[200px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="font-semibold text-blue-600">{data.project_name}</span>,
	},
	{
		id: "generated_by_name",
		label: "generated by",
		cellClassName: "min-w-[160px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="font-medium">{data.generated_by_name || "—"}</span>,
	},
	{
		id: "created_at",
		label: "generated at",
		cellClassName: "min-w-[160px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) =>
			data.created_at ? (
				<span className="font-medium">
					{new Date(data.created_at).toLocaleString(undefined, {
						year: "numeric",
						month: "short",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					})}
				</span>
			) : (
				<span>—</span>
			),
	},
	{
		id: "item_count",
		label: "items",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.item_count ?? 0}</span>,
	},
	{
		id: "supplier_grand_total",
		label: "supplier total",
		cellClassName: "min-w-[120px] text-right",
		headerClassName: "whitespace-nowrap text-right",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.supplier_grand_total)}</span>,
	},
	{
		id: "company_grand_total",
		label: "company total",
		cellClassName: "min-w-[120px] text-right",
		headerClassName: "whitespace-nowrap text-right",
		cell: ({ data }) => <span className="font-medium">₱{formatNumber(data.company_grand_total)}</span>,
	},
	
	{
		id: "file",
		label: "file",
		cellClassName: "min-w-[120px] text-center pl-6",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => {
			const items = (data.items ?? []) as Record<string, unknown>[];
			if (items.length === 0) {
				return <span className="text-gray-400">—</span>;
			}
			const buildExportData = () => {
				const { headers, rows, supplierGrandTotal, companyGrandTotal, filename } = prepareBomDataForExport(
					items,
					data.project_name || "Untitled",
				);
				const totalsRow = headers.map((h) => {
					if (h === "Supplier Total") return supplierGrandTotal;
					if (h === "Company Total") return companyGrandTotal;
					if (h === "Brand / Model") return "GRAND TOTAL";
					return "";
				});
				return { headers, rows: [...rows, totalsRow], filename };
			};
			return (
				<div className="flex items-center justify-center gap-2">
					<button
						type="button"
						onClick={() => viewGeneratedBomPdf(data.project_name || "Untitled", items)}
						className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
						title="View PDF"
					>
						<IconEye size={16} />
					</button>
					<button
						type="button"
						onClick={() => downloadGeneratedBomPdf(data.project_name || "Untitled", items)}
						className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
						title="Download PDF"
					>
						<IconFileTypePdf size={14} stroke={2} />
						<span>PDF</span>
					</button>
				</div>
			);
		},
	},
];

export const Route = createFileRoute("/_dashboard/bom")({
	validateSearch: z.object({
		tab: z.enum(["items", "generated"]).optional(),
		"filter[search]": z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const activeTab = search.tab ?? "items";
	const [editingBom, setEditingBom] = useState<Bom | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isGenerateBomOpen, setIsGenerateBomOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const deleteBomMutation = useBomDeleteMutation();
	const deleteGeneratedBomMutation = useGeneratedBomDeleteMutation();

	const bomsQuery = useQuery(
		bomsQueryOptions({
			...search,
		}),
	);

	const generatedBomsQuery = useQuery(
		generatedBomsQueryOptions({
			...search,
		}),
	);

	const handleTabChange = async (value: string) => {
		await navigate({
			to: "/bom",
			search: () => ({
				tab: value === "generated" ? "generated" : undefined,
				page: 1,
			}),
		});
		setSearchTerm("");
	};

	// Edit
	const handleEditBom = (bom: Bom) => {
		setEditingBom(bom);
		setIsEditModalOpen(true);
	};
	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingBom(null);
	};

	// Create/Add
	const handleCreateBom = () => {
		setIsCreateModalOpen(true);
	};
	const handleCloseCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	// Generate Bom
	const handleCloseGenerateBom = () => {
		setIsGenerateBomOpen(false);
	};
	const handleGenerateBomSuccess = () => {
		bomsQuery.refetch();
		generatedBomsQuery.refetch();
	};
	// Success create
	const handleCreateSuccess = () => {
		bomsQuery.refetch();
	};
	const handleEditSuccess = () => {
		bomsQuery.refetch();
	};

	// Delete
	const handleDeleteBom = async (bom: Bom) => {
		if (bom.id) {
			await deleteBomMutation.mutateAsync(bom.id.toString());
			await queryClient.invalidateQueries({
				queryKey: ["bom"],
			});
		}
	};

	const handleDeleteGeneratedBom = async (gbom: GeneratedBom) => {
		if (gbom.id) {
			await deleteGeneratedBomMutation.mutateAsync(gbom.id.toString());
		}
	};

	// Search
	const handleSearch = async () => {
		await navigate({
			to: "/bom",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};
	const handleSearchKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};
	const clearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/bom",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const boms = bomsQuery.data?.data || [];
	const bomsMeta = bomsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
	};

	const generatedBoms = generatedBomsQuery.data?.data || [];
	const generatedMeta = generatedBomsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
	};

	const searchPlaceholder =
		activeTab === "generated" ? "Search project name or generated by..." : "Search item name, brand, supplier name...";

	if (bomsQuery.isLoading) {
		return (
			<Content pageName="Bom">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading Bill of Materials...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Bom">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Bill of Materials (BOM)</span>
						<span className="text-base text-foreground/70">Manage your Bill of Materials database</span>
					</div>
					<div className="flex gap-2">
						<Button onClick={() => setIsGenerateBomOpen(true)}>
							<IconPlus size={18} stroke={2} />
							<span className="">Generate BOM</span>
						</Button>
						<Button onClick={handleCreateBom}>
							<IconPlus size={18} stroke={2} />
							<span className="">Add New BOM</span>
						</Button>
					</div>
				</div>

				<Tabs value={activeTab} onValueChange={handleTabChange} variant="compact">
					<TabsList>
						<TabsTrigger value="items">BOM Item</TabsTrigger>
						<TabsTrigger value="generated">Generated BOM</TabsTrigger>
					</TabsList>

					<TabsContent value="items" className="pt-4">
						<div className="flex flex-col w-full gap-4">
							<div className="flex items-center gap-2 w-full max-w-md">
								<div className="relative flex-1">
									<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
									<input
										type="text"
										placeholder={searchPlaceholder}
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										onKeyPress={handleSearchKeyPress}
										className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
									{searchTerm && (
										<button
											type="button"
											onClick={clearSearch}
											className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
										>
											×
										</button>
									)}
								</div>
								<Button onClick={handleSearch} variant="outline">
									Search
								</Button>
							</div>
							<DataTable
								className="border-collapse capitalize"
								data={boms}
								columns={columns}
								stickyHead={false}
								stickyCol={false}
								showBorder={true}
								compact={true}
								showToolbar={false}
								onEdit={handleEditBom}
								onDelete={(bom) => {
									handleDeleteBom(bom);
								}}
							/>
							<Pagination
								onClick={async (page) => {
									await navigate({
										to: "/bom",
										search: (old) => ({
											...old,
											page,
										}),
									});
								}}
								initialPage={bomsMeta.current_page}
								pageCount={bomsMeta.last_page}
								totalItemCount={bomsMeta.total}
							/>
						</div>
					</TabsContent>

					<TabsContent value="generated" className="pt-4">
						<div className="flex flex-col w-full gap-4">
							<div className="flex items-center gap-2 w-full max-w-md">
								<div className="relative flex-1">
									<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
									<input
										type="text"
										placeholder={searchPlaceholder}
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										onKeyPress={handleSearchKeyPress}
										className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
									{searchTerm && (
										<button
											type="button"
											onClick={clearSearch}
											className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
										>
											×
										</button>
									)}
								</div>
								<Button onClick={handleSearch} variant="outline">
									Search
								</Button>
							</div>
							<DataTable
								className="border-collapse capitalize"
								data={generatedBoms}
								columns={generatedBomColumns}
								stickyHead={false}
								stickyCol={false}
								showBorder={true}
								compact={true}
								showToolbar={false}
								onDelete={(gbom) => {
									handleDeleteGeneratedBom(gbom);
								}}
							/>
							<Pagination
								onClick={async (page) => {
									await navigate({
										to: "/bom",
										search: (old) => ({
											...old,
											page,
										}),
									});
								}}
								initialPage={generatedMeta.current_page}
								pageCount={generatedMeta.last_page}
								totalItemCount={generatedMeta.total}
							/>
						</div>
					</TabsContent>
				</Tabs>
			</div>
			<GenerateBomModal isOpen={isGenerateBomOpen} onClose={handleCloseGenerateBom} onSuccess={handleGenerateBomSuccess} />
			<CreateBomModal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} onSuccess={handleCreateSuccess} />
			{editingBom && <EditBomModal bom={editingBom} isOpen={isEditModalOpen} onClose={handleCloseEditModal} onSuccess={handleEditSuccess} />}
		</Content>
	);
}
